'use client';

import dynamic from 'next/dynamic';
import { useState } from 'react';

import { MenuBulletHorizontal, PlayCircle } from '@zoomit/icons';
import { Image } from '@zoomit/primitives';
import { cn } from '@zoomit/primitives';
import type { GalleryItem} from '@zoomit/web-kit';
import { BaseMediaUrl, useGallery, VideoCircle, ProductDefaultCover } from '@zoomit/web-kit';
import { cssVar, theme } from '@zoomit/zoomit-ds';

const Gallery = dynamic(() => import('@zoomit/web-kit').then((mod) => mod.Gallery), {
  ssr: false,
});

type Props = {
  gallery: GalleryItem[];
  productId: string;
};

const ProductMediaPreviewDesktop = (props: Props) => {
  const { gallery, productId } = props;

  const [mainMedia, setMainMedia] = useState<GalleryItem>(gallery.find((media) => media.type === 'Image')!);
  const [mainMediaIndex, setMainMediaIndex] = useState(gallery.findIndex((media) => media.type === 'Image'));

  const { handleCloseGallery, handleOpenGallery, isGalleryOpen } = useGallery({ galleryId: productId });

  if (gallery.length === 0) {
    return (
      <div className="w-[311px]">
        <div className="aspect-primary relative flex w-full cursor-pointer items-center justify-center">
          <Image
            src={ProductDefaultCover}
            fill
            alt={'prview-default-cover-img'}
            placeholder="blur"
            sizes="320px"
            className="object-contain"
          />
        </div>
      </div>
    );
  }

  const isMainMediaImage = mainMedia.type === 'Image';

  return (
    <div className="w-[311px]">
      <div className="aspect-primary relative flex w-full cursor-pointer items-center justify-center">
        <Image
          src={
            isMainMediaImage
              ? `${BaseMediaUrl}/${mainMedia.image.id}`
              : `${BaseMediaUrl}/${mainMedia.video.coverImage.id}`
          }
          fill
          alt={isMainMediaImage ? mainMedia.image.alt : mainMedia.video.coverImage.alt}
          placeholder="blur"
          blurDataURL={isMainMediaImage ? mainMedia.image.preview : mainMedia.video.coverImage.preview}
          onClick={() => handleOpenGallery(mainMediaIndex)}
          sizes="320px"
          className="object-contain"
          quality={80}
        />
        {!isMainMediaImage && (
          <PlayCircle
            size="lx"
            fill={cssVar(theme.white)}
            className="absolute top-[50%] left-[50%] z-10 -translate-x-2/4 -translate-y-2/4"
          />
        )}
      </div>

      <div className="mt-6 flex h-fit w-fit justify-between gap-2">
        {gallery.slice(0, 5).map((media, index) =>
          media.type === 'Image' ? (
            <button
              className={cn(
                'relative flex h-[56px] w-[56px] cursor-pointer rounded-md border p-1',
                index === mainMediaIndex ? 'border-primary2' : 'border-grey6',
              )}
              key={index}
              onClick={() => {
                if (index === 4) {
                  handleOpenGallery(index);
                } else {
                  setMainMedia(media);
                  setMainMediaIndex(index);
                }
              }}
            >
              <div className="relative inline-block h-[46px] w-[46px]">
                <Image
                  className={cn('object-cover duration-150 hover:brightness-90', index === 4 && 'blur-[2px]')}
                  src={`${BaseMediaUrl}/${media.image.id}`}
                  fill
                  alt={media.image.alt}
                  placeholder="blur"
                  blurDataURL={media.image.preview}
                  sizes="96px"
                />
              </div>
              {index === 4 && (
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <MenuBulletHorizontal fill={cssVar(theme.white)} />
                </div>
              )}
            </button>
          ) : (
            <div
              key={`product-video-preview-${index}`}
              className="relative cursor-pointer"
              onClick={() => handleOpenGallery(index)}
            >
              <PlayCircle
                fill={cssVar(theme.white)}
                className="absolute top-[50%] left-[50%] z-10 -translate-x-2/4 -translate-y-2/4"
              />
              <VideoCircle size="md" cover={media.video.coverImage} />
            </div>
          ),
        )}
      </div>
      {isGalleryOpen && <Gallery items={gallery} onCloseGallery={handleCloseGallery} />}
    </div>
  );
};

export default ProductMediaPreviewDesktop;
